import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'

interface MenuProps {
  // Содержимое триггера (текст/чипы). Если оно не текстовое — задай ariaLabel.
  trigger: ReactNode
  ariaLabel?: string
  // Класс корня — когда меню занимает слот целиком (панель свойств), а не
  // сжимается по ширине содержимого триггера.
  className?: string
  triggerClassName?: string
  panelClassName?: string
  align?: 'start' | 'end'
  // Панель раскрывается вниз (default) или вверх — для триггеров у нижней кромки.
  side?: 'bottom' | 'top'
  caret?: boolean
  // Панель получает close: пункт закрывает меню после выбора.
  children: (close: () => void) => ReactNode
}

// Поповер-меню: триггер + плавающая панель. Закрытие — Esc (фокус назад на
// триггер), клик снаружи, выбор пункта. Роли ARIA не навязываются панели —
// семантику (menuitem/кнопки/поиск) задаёт потребитель; стрелки навигируют по
// элементам с [data-menu-item].
export function Menu({
  trigger,
  ariaLabel,
  className,
  triggerClassName,
  panelClassName,
  align = 'start',
  side = 'bottom',
  caret = false,
  children,
}: MenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const close = () => setOpen(false)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      const items = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>('[data-menu-item]') ?? [],
      )
      if (items.length === 0) return
      e.preventDefault()
      const idx = items.indexOf(document.activeElement as HTMLElement)
      const next =
        e.key === 'ArrowDown'
          ? idx < 0
            ? 0
            : Math.min(idx + 1, items.length - 1)
          : Math.max(idx - 1, 0)
      items[next]?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const first = panelRef.current?.querySelector<HTMLElement>('input, [data-menu-item]')
    first?.focus()
  }, [open])

  return (
    <div ref={rootRef} className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          // py-2 на таче: триггер меню («…») иначе высотой ~26px — не попасть пальцем.
          'inline-flex items-center gap-1 rounded-control px-2 py-0.5 text-text-primary transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent touch:-my-2 touch:py-2',
          triggerClassName,
        )}
      >
        {trigger}
        {caret && (
          <Icon
            name="chevron-down"
            size={14}
            className={cn('shrink-0 text-text-tertiary transition-transform', open && 'rotate-180')}
          />
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          className={cn(
            // max-w по вьюпорту: панель min-w-56 у правой кромки телефона иначе
            // уезжает за экран (портала и флипа здесь нет).
            'absolute z-30 flex max-w-[calc(100vw-2rem)] min-w-56 flex-col rounded-card border border-border-default bg-bg-elevated p-1',
            side === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
            align === 'end' ? 'right-0' : 'left-0',
            panelClassName,
          )}
        >
          {children(close)}
        </div>
      )}
    </div>
  )
}

interface MenuItemProps {
  onSelect: () => void
  selected?: boolean
  children: ReactNode
  className?: string
}

// Строка меню: настоящая button (tab/Enter/Space из коробки) + отметка выбора.
// Роли menuitem нет намеренно — панель может содержать и <input> (поиск), что для
// role=menu невалидно; кнопки доступны и без обёртки-меню. data-menu-item — якорь
// навигации стрелками в Menu, aria-current — «текущий в наборе».
export function MenuItem({ onSelect, selected = false, children, className }: MenuItemProps) {
  return (
    <button
      type="button"
      data-menu-item
      {...(selected ? { 'aria-current': true } : {})}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-2 rounded-control px-2.5 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-raised hover:text-text-primary focus-visible:bg-bg-raised focus-visible:text-text-primary focus-visible:outline-none',
        selected && 'text-text-primary',
        className,
      )}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {selected && <Icon name="check" size={14} className="shrink-0 text-accent" />}
    </button>
  )
}
